import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';
import { equipmentModelService } from '@/services/equipment-model.service';
import { useDebounce } from '@/hooks/use-debounce';

export interface EquipmentModelOption {
  id: string;
  modelNumber: string;
  name: string | null;
  manufacturerName: string;
  equipmentTypeName: string;
}

/** Inline breadcrumb for committed selection: "Atlas Copco > Oil Injected... > GA75" */
function formatDisplay(opt: EquipmentModelOption): string {
  return `${opt.manufacturerName} > ${opt.equipmentTypeName} > ${opt.modelNumber}`;
}

const INPUT_CLASS =
  'flex h-10 w-full rounded-lg border border-input bg-card pl-9 pr-9 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50';

export function EquipmentModelCombobox({
  value,
  onChange,
  disabled,
  placeholder = 'Search by model number, manufacturer, or type…',
}: {
  value: EquipmentModelOption | null;
  onChange: (model: EquipmentModelOption | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [inputValue, setInputValue] = useState(value ? formatDisplay(value) : '');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounced = useDebounce(inputValue.trim(), 300);
  const containerRef = useRef<HTMLDivElement>(null);
  // Prevents the value-sync useEffect from clobbering text the user is actively typing
  const isInternalChangeRef = useRef(false);
  const listboxId = useId();

  const enabled = open && debounced.length >= 1;

  const { data, isFetching } = useQuery({
    queryKey: ['equipment-models-search', debounced],
    queryFn: () => equipmentModelService.list({ search: debounced, pageSize: 15 }),
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const results = enabled ? (data?.items ?? []) : [];

  // Sync display text when value changes externally (e.g. template loaded into edit form)
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
    setInputValue(value ? formatDisplay(value) : '');
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const commit = (item: (typeof results)[number]) => {
    const option: EquipmentModelOption = {
      id: item.id,
      modelNumber: item.modelNumber,
      name: item.name,
      manufacturerName: item.manufacturerName,
      equipmentTypeName: item.equipmentTypeName,
    };
    isInternalChangeRef.current = true;
    onChange(option);
    setInputValue(formatDisplay(option));
    setOpen(false);
    setActiveIndex(-1);
  };

  const clear = () => {
    isInternalChangeRef.current = true;
    onChange(null);
    setInputValue('');
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleChange = (text: string) => {
    setInputValue(text);
    setOpen(true);
    setActiveIndex(-1);
    if (value) {
      isInternalChangeRef.current = true;
      onChange(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0 && results[activeIndex]) {
        e.preventDefault();
        commit(results[activeIndex]!);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const showDropdown = open && debounced.length >= 1;

  return (
    <div ref={containerRef} className="relative">
      <FontAwesomeIcon
        icon={faSearch}
        className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
      />
      <input
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined}
        value={inputValue}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        className={INPUT_CLASS}
        autoComplete="off"
      />
      {isFetching ? (
        <FontAwesomeIcon
          icon={faSpinner}
          className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground"
        />
      ) : inputValue && !disabled ? (
        <button
          type="button"
          onClick={clear}
          className="absolute right-2.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:text-destructive"
          aria-label="Clear equipment model selection"
        >
          <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
        </button>
      ) : null}

      {showDropdown && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-border bg-card py-1 shadow-lg"
        >
          {results.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              {isFetching ? 'Searching…' : 'No models found.'}
            </li>
          ) : (
            results.map((item, i) => (
              <li
                key={item.id}
                id={`${listboxId}-opt-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(item);
                }}
                onMouseEnter={() => setActiveIndex(i)}
                className={`cursor-pointer px-3 py-2.5 ${i === activeIndex ? 'bg-muted' : ''}`}
              >
                <div className="text-xs text-muted-foreground">{item.manufacturerName}</div>
                <div className="text-xs text-muted-foreground">{item.equipmentTypeName}</div>
                <div className="text-sm font-semibold text-foreground">{item.modelNumber}</div>
                {item.name && <div className="text-xs text-muted-foreground">{item.name}</div>}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
